/**
 * The chat panel's selectable theme.
 *
 * A theme is nothing but a set of CSS custom property values. Chatbot.vue puts the selected
 * theme's values on the panel's container element, and every color in the chat - the panel's own
 * styles, the styles handed to Deep Chat as JS objects, and the styles inside the custom elements
 * Deep Chat renders - is written as a var() reference to one of them.
 *
 * Theming this way rather than by swapping the style objects themselves is what lets a theme
 * change take effect immediately: custom properties inherit through shadow boundaries, so the
 * declarations Deep Chat has already applied - including the ones on messages it rendered earlier
 * - resolve against whatever the container currently defines, with no re-render needed.
 *
 * Values come from Element Plus's own variables wherever one fits, so the light theme in
 * particular tracks the rest of the app rather than restating its palette.
 *
 * Two things below are shaped by how Deep Chat consumes these values rather than by design. The
 * panel's background is split into a color and an image because a shorthand cannot carry a var()
 * into an inline style safely - the comment on Chatbot.vue's `style` computed has the details. And
 * every border is the shared --chatbot-border-width plus a color per element, which keeps the High
 * Contrast theme's thicker outlines in one declaration rather than a rule to remember at each
 * border. Consumers compose the two.
 */

// The current scheme, kept as-is: its teal gradient is the chat's established look, and the
// hard-coded values below are the ones these variables were extracted from.
const darkThemeVars = {
  '--chatbot-border-width': '1px',

  '--chatbot-panel-background-color': '#0a1d28',
  '--chatbot-panel-background-image': 'linear-gradient(to right, teal, 10%, #0a1d28 75%)',
  '--chatbot-panel-border-color': '#007575',
  '--chatbot-panel-shadow': '5px 0px 12px 4px teal',
  '--chatbot-resize-handle-background': 'rgba(0, 128, 128, 0.4)',
  '--chatbot-scrollbar-thumb-background': 'rgba(0, 128, 128, 0.6)',

  '--chatbot-text-color': '#fff',
  '--chatbot-text-color-muted': 'rgba(255, 255, 255, 0.7)',
  '--chatbot-link-color': '#0e75bc',

  '--chatbot-bubble-shadow': 'teal 0 7px 16px -5px',
  '--chatbot-ai-bubble-background': 'rgba(10, 29, 40, 0.4)',
  '--chatbot-ai-bubble-border-color': 'rgba(10, 29, 40, 0.2)',
  '--chatbot-user-bubble-background': 'rgba(255, 255, 255, 0.2)',
  '--chatbot-user-bubble-border-color': 'rgba(255, 255, 255, 0.2)',
  '--chatbot-error-bubble-background': '#c54242',
  '--chatbot-error-bubble-color': '#fff',
  '--chatbot-error-bubble-border-color': '#c54242',
  '--chatbot-loading-color': 'teal',

  // The text input and the suggested prompt buttons are one shared treatment: this at rest, the
  // highlight pair below while the input holds focus or a prompt is under the pointer.
  '--chatbot-surface-background': 'rgba(255, 255, 255, 0.1)',
  '--chatbot-surface-border-color': 'rgba(255, 255, 255, 0.3)',
  '--chatbot-placeholder-color': 'rgba(255, 255, 255, 0.4)',

  // Both surfaces rest on the same background, so one value lightens them by the same amount.
  '--chatbot-highlight-background': 'rgba(255, 255, 255, 0.2)',
  '--chatbot-highlight-border-color': 'rgba(255, 255, 255, 0.8)',

  '--chatbot-icon-color': '#fff',
  '--chatbot-icon-color-muted': 'rgba(255, 255, 255, 0.4)',
  // The same background colors Deep Chat uses for its own icon hover and active states.
  '--chatbot-icon-hover-background': '#9c9c9c2e',
  '--chatbot-icon-active-background': '#9c9c9c5e',
  '--chatbot-toolbar-icon-hover-background': '#9c9c9c2e',
  '--chatbot-toolbar-icon-active-background': '#9c9c9c5e',
  '--chatbot-focus-ring-color': 'rgba(255, 255, 255, 0.6)',

  // Only a prompt has a pressed state; the input has none.
  '--chatbot-prompt-active-background': 'rgba(255, 255, 255, 0.3)',

  '--chatbot-positive-color': '#4ade80',
  '--chatbot-positive-color-pressed': '#22c55e',
  '--chatbot-positive-color-selected-hover': '#86efac',
  '--chatbot-negative-color': '#f87171',
  '--chatbot-negative-color-pressed': '#ef4444',
  '--chatbot-negative-color-selected-hover': '#fca5a5',

  '--chatbot-approve-background': 'teal',
  '--chatbot-approve-hover-background': '#009999',
  '--chatbot-approve-active-background': '#007575',
  '--chatbot-approve-color': '#fff',
  '--chatbot-reject-background': 'rgba(255, 255, 255, 0.2)',
  '--chatbot-reject-hover-background': 'rgba(255, 255, 255, 0.3)',
  '--chatbot-reject-active-background': 'rgba(255, 255, 255, 0.4)',
  '--chatbot-reject-color': '#fff',

  '--chatbot-inset-background': 'rgba(0, 0, 0, 0.25)',
  '--chatbot-inset-border-color': 'rgba(255, 255, 255, 0.2)',
};

// A white panel with dark text, built out of the Element Plus variables the rest of the app is
// styled from so it reads as part of the same UI rather than as a separate surface.
const lightThemeVars = {
  '--chatbot-border-width': '1px',

  '--chatbot-panel-background-color': 'var(--el-bg-color)',
  '--chatbot-panel-background-image': 'none',
  '--chatbot-panel-border-color': 'var(--el-border-color)',
  '--chatbot-panel-shadow': '5px 0px 12px 4px rgba(0, 0, 0, 0.08)',
  '--chatbot-resize-handle-background': 'var(--el-color-primary-light-7)',
  '--chatbot-scrollbar-thumb-background': 'var(--el-border-color-dark)',

  '--chatbot-text-color': 'var(--el-text-color-primary)',
  // Element Plus's secondary text is only 3.1:1 against a white panel, which reads fine for a
  // table label but not for the intro description this styles, so muted text steps up to the
  // regular text color and clears AA at 5.6:1.
  '--chatbot-text-color-muted': 'var(--el-text-color-regular)',
  // The blue the app uses for links elsewhere, which also clears AA against a white panel.
  '--chatbot-link-color': '#0e75bc',

  '--chatbot-bubble-shadow': 'rgba(0, 0, 0, 0.1) 0 4px 12px -5px',
  '--chatbot-ai-bubble-background': 'var(--el-fill-color-light)',
  '--chatbot-ai-bubble-border-color': 'var(--el-border-color-light)',
  '--chatbot-user-bubble-background': 'var(--el-color-primary-light-9)',
  '--chatbot-user-bubble-border-color': 'var(--el-color-primary-light-7)',
  '--chatbot-error-bubble-background': 'var(--el-color-danger-light-9)',
  // Element Plus's danger red is too light to read on its own tint, so error text keeps the
  // darker red the chat already used for error bubbles.
  '--chatbot-error-bubble-color': '#c54242',
  '--chatbot-error-bubble-border-color': 'var(--el-color-danger-light-5)',
  '--chatbot-loading-color': 'var(--el-color-primary)',

  '--chatbot-surface-background': 'var(--el-fill-color-blank)',
  '--chatbot-surface-border-color': 'var(--el-border-color)',
  '--chatbot-placeholder-color': 'var(--el-text-color-placeholder)',

  '--chatbot-highlight-background': 'var(--el-fill-color-light)',
  '--chatbot-highlight-border-color': 'var(--el-color-primary)',

  '--chatbot-icon-color': 'var(--el-text-color-regular)',
  '--chatbot-icon-color-muted': 'var(--el-text-color-placeholder)',
  // Translucent rather than one of Element Plus's fill grays, like the other themes, so the
  // control tints whatever it happens to sit over instead of punching a flat patch through it.
  // These land on the same color those fills did while the panel's own background is behind them.
  '--chatbot-icon-hover-background': 'rgba(0, 0, 0, 0.06)',
  '--chatbot-icon-active-background': 'rgba(0, 0, 0, 0.1)',
  // The response toolbar's buttons sit directly on the white panel rather than over a message
  // bubble, where Element Plus's fill colors - only a few percent off white - are too faint to
  // register as a hover. Its border grays are the lightest values that still read as one.
  '--chatbot-toolbar-icon-hover-background': 'var(--el-border-color)',
  '--chatbot-toolbar-icon-active-background': 'var(--el-border-color-darker)',
  '--chatbot-focus-ring-color': 'var(--el-color-primary)',

  '--chatbot-prompt-active-background': 'var(--el-fill-color)',

  // Element Plus's success and danger colors are tuned for text on white, so the rating icons use
  // their darkened variants to stay clear of the 3:1 threshold for graphics.
  '--chatbot-positive-color': 'var(--el-color-success-dark-2)',
  '--chatbot-positive-color-pressed': '#3e7522',
  '--chatbot-positive-color-selected-hover': 'var(--el-color-success)',
  '--chatbot-negative-color': 'var(--el-color-danger-dark-2)',
  '--chatbot-negative-color-pressed': '#a63838',
  '--chatbot-negative-color-selected-hover': 'var(--el-color-danger)',

  '--chatbot-approve-background': 'var(--el-color-primary)',
  '--chatbot-approve-hover-background': 'var(--el-color-primary-light-3)',
  '--chatbot-approve-active-background': 'var(--el-color-primary-dark-2)',
  '--chatbot-approve-color': 'var(--el-color-white)',
  // Reject is a filled circle with no border, so it has to darken the white panel enough to read
  // as a button at all and then step far enough on hover and press to be felt. Element Plus's fill
  // grays are all within a few percent of white, so these come off its border and gray scale
  // instead - roughly five steps of L* apart - with the darkest text color on top to stay well
  // clear of AA on all three.
  '--chatbot-reject-background': 'var(--el-border-color)',
  '--chatbot-reject-hover-background': 'var(--el-border-color-darker)',
  '--chatbot-reject-active-background': 'var(--el-text-color-disabled)',
  '--chatbot-reject-color': 'var(--el-text-color-primary)',

  '--chatbot-inset-background': 'var(--el-fill-color)',
  '--chatbot-inset-border-color': 'var(--el-border-color-light)',
};

// Black panel, white text, and controls told apart by outlines rather than by subtle differences
// in background. This is the one theme allowed to look unlike the rest of the app: body text sits
// at 21:1 against the panel, the muted and placeholder grays stay above 10:1, and every accent -
// cyan for links, yellow for focus, green and red for ratings - clears 7:1 against black.
const highContrastThemeVars = {
  // Double the other themes' width: an outline is what tells this theme's controls apart, since
  // their backgrounds are all the same black.
  '--chatbot-border-width': '2px',

  '--chatbot-panel-background-color': 'var(--el-color-black)',
  '--chatbot-panel-background-image': 'none',
  '--chatbot-panel-border-color': 'var(--el-color-white)',
  '--chatbot-panel-shadow': '5px 0px 12px 4px rgba(0, 0, 0, 0.9)',
  '--chatbot-resize-handle-background': '#ffff00',
  '--chatbot-scrollbar-thumb-background': 'var(--el-color-white)',

  '--chatbot-text-color': 'var(--el-color-white)',
  '--chatbot-text-color-muted': '#e0e0e0',
  '--chatbot-link-color': '#00ffff',

  '--chatbot-bubble-shadow': 'none',
  '--chatbot-ai-bubble-background': 'var(--el-color-black)',
  '--chatbot-ai-bubble-border-color': 'var(--el-color-white)',
  // The same lift the text input takes while it holds focus, which gives a user message a cue
  // that survives without color - the AI's bubble stays flat black - on top of its cyan outline.
  '--chatbot-user-bubble-background': 'rgba(255, 255, 255, 0.15)',
  '--chatbot-user-bubble-border-color': '#00ffff',
  '--chatbot-error-bubble-background': 'var(--el-color-black)',
  '--chatbot-error-bubble-color': '#ff9b9b',
  '--chatbot-error-bubble-border-color': '#ff9b9b',
  '--chatbot-loading-color': '#ffff00',

  '--chatbot-surface-background': 'var(--el-color-black)',
  '--chatbot-surface-border-color': 'var(--el-color-white)',
  '--chatbot-placeholder-color': '#d0d0d0',

  '--chatbot-highlight-background': 'rgba(255, 255, 255, 0.15)',
  '--chatbot-highlight-border-color': '#ffff00',

  '--chatbot-icon-color': 'var(--el-color-white)',
  '--chatbot-icon-color-muted': '#a6a6a6',
  '--chatbot-icon-hover-background': 'rgba(255, 255, 255, 0.3)',
  '--chatbot-icon-active-background': 'rgba(255, 255, 255, 0.45)',
  '--chatbot-toolbar-icon-hover-background': 'rgba(255, 255, 255, 0.3)',
  '--chatbot-toolbar-icon-active-background': 'rgba(255, 255, 255, 0.45)',
  '--chatbot-focus-ring-color': '#ffff00',

  '--chatbot-prompt-active-background': 'rgba(255, 255, 255, 0.25)',

  '--chatbot-positive-color': '#4ade80',
  '--chatbot-positive-color-pressed': '#86efac',
  '--chatbot-positive-color-selected-hover': '#bbf7d0',
  '--chatbot-negative-color': '#f87171',
  '--chatbot-negative-color-pressed': '#fca5a5',
  '--chatbot-negative-color-selected-hover': '#fecaca',

  // Approve and reject are filled with a light color and given a dark glyph, which stands out
  // more on an already-black panel than another outlined button would.
  '--chatbot-approve-background': '#4ade80',
  '--chatbot-approve-hover-background': '#86efac',
  '--chatbot-approve-active-background': '#bbf7d0',
  '--chatbot-approve-color': 'var(--el-color-black)',
  '--chatbot-reject-background': 'var(--el-color-white)',
  '--chatbot-reject-hover-background': '#e0e0e0',
  '--chatbot-reject-active-background': '#c7c7c7',
  '--chatbot-reject-color': 'var(--el-color-black)',

  '--chatbot-inset-background': 'var(--el-color-black)',
  '--chatbot-inset-border-color': 'var(--el-color-white)',
};

/**
 * The selectable themes, in the order they're offered to the user. A theme's name is what gets
 * persisted, so anyone who had a since-renamed theme selected falls back to the default.
 */
export const CHATBOT_THEMES = [
  { name: 'dark', label: 'Dark', vars: darkThemeVars },
  { name: 'light', label: 'Light', vars: lightThemeVars },
  { name: 'high-contrast', label: 'High Contrast', vars: highContrastThemeVars },
];

export const DEFAULT_CHATBOT_THEME = CHATBOT_THEMES[0].name;

/**
 * The given theme name if it's still one of the themes above, otherwise the default. Guards
 * against a name persisted by an older version of the app that has since been renamed.
 */
export function resolveChatbotTheme(name) {
  return CHATBOT_THEMES.some(theme => theme.name === name) ? name : DEFAULT_CHATBOT_THEME;
}

/**
 * The CSS custom properties a theme applies, ready to spread into a style binding.
 */
export function chatbotThemeVars(name) {
  const resolvedName = resolveChatbotTheme(name);
  return CHATBOT_THEMES.find(theme => theme.name === resolvedName).vars;
}
